import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  mode: "light",
  user: null,
  users: { data: [], total: 0 },
  viewingUser: null,
  token: null,
  organisation: null,
  organisations: { data: [], total: 0 },
  organisationSearchResults: [],
  location: null,
  locations: { data: [], total: 0 },
  locationsAll: [],
  locationSearchResults: [],
  division: null,
  divisions: { data: [], total: 0 },
  divisionSearchResults: [],
  divisionsForOrganisation: [],
  level: null,
  levels: { data: [], total: 0 },
  levelSearchResults: [],
  levelsForOrganisation: [],
  snackbar: {
    open: false,
    message: "",
    severity: "error",
  },
  roles: [],
  principle: null,
  principles: [],
  principleSearchResults: [],
  principleQuestion: null,
  principleQuestions: { data: [], total: 0 },
  principleQuestionsSearchResults: [],
  assessment: null,
  assessmentReports: {
    byPrinciple: [],
    byQuestion: [],
    totalAssessments: 0,
  },
  assessors: [],
  assessees: [],
  assesseeReports: {
    rows: [],
    total: 0,
  },
  assessments: { data: [], total: 0 },
  guide: null,
  guides: [],
  projectOverview: null,
  projectOverviews: [],
  guideDeliberatePractice: null,
  guideDeliberatePractices: [],
  guideInstructionalCoaching: null,
  guideInstructionalCoachings: [],
  coachingFeed: {
    data: [],
    total: 0,
  },
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setMode: (state) => {
      state.mode = state.mode === "light" ? "dark" : "light";
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setViewingUser: (state, action) => {
      state.viewingUser = action.payload;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    setLogin: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
    },
    setLogout: (state) => {
      return initialState;
    },
    setOrganisation: (state, action) => {
      state.organisation = action.payload;
    },
    setOrganisations: (state, action) => {
      state.organisations = action.payload;
    },
    setOrganisationSearchResults: (state, action) => {
      state.organisationSearchResults = action.payload;
    },
    setLocation: (state, action) => {
      state.location = action.payload;
    },
    setLocations: (state, action) => {
      state.locations = action.payload;
    },
    setLocationSearchResults: (state, action) => {
      state.locationSearchResults = action.payload;
    },
    setLocationsAll: (state, action) => {
      state.locationsAll = action.payload;
    },
    setDivision: (state, action) => {
      state.division = action.payload;
    },
    setDivisions: (state, action) => {
      state.divisions = action.payload;
    },
    setDivisionSearchResults: (state, action) => {
      state.divisionSearchResults = action.payload;
    },
    setDivisionsForOrganisation: (state, action) => {
      state.divisionsByLocation = action.payload;
    },
    setLevel: (state, action) => {
      state.level = action.payload;
    },
    setLevels: (state, action) => {
      state.levels = action.payload;
    },
    setLevelSearchResults: (state, action) => {
      state.levelSearchResults = action.payload;
    },
    setLevelsForOrganisation: (state, action) => {
      state.levelsForOrganisation = action.payload;
    },
    setSnackbar: (state, action) => {
      state.snackbar = action.payload;
    },
    setRoles: (state, action) => {
      state.roles = action.payload;
    },
    setPrinciple: (state, action) => {
      state.principle = action.payload;
    },
    setPrinciples: (state, action) => {
      state.principles = action.payload;
    },
    setPrincipleSearchResults: (state, action) => {
      state.principleSearchResults = action.payload;
    },
    setPrincipleQuestion: (state, action) => {
      state.principleQuestion = action.payload;
    },
    setPrincipleQuestions: (state, action) => {
      state.principleQuestions = action.payload;
    },
    setPrincipleQuestionsSearchResults: (state, action) => {
      state.principleQuestionsSearchResults = action.payload;
    },
    setAssessment: (state, action) => {
      state.assessment = action.payload;
    },
    setAssessmentReports: (state, action) => {
      state.assessmentReports = action.payload;
    },
    setAssessors: (state, action) => {
      state.assessors = action.payload;
    },
    setAssessees: (state, action) => {
      state.assessees = action.payload;
    },
    setAssesseeReports: (state, action) => {
      state.assesseeReports = action.payload;
    },
    setAssessments: (state, action) => {
      state.assessments = action.payload;
    },
    setGuide: (state, action) => {
      state.guide = action.payload;
    },
    setGuides: (state, action) => {
      state.guides = action.payload;
    },
    setProjectOverview: (state, action) => {
      state.projectOverview = action.payload;
    },
    setProjectOverviews: (state, action) => {
      state.projectOverviews = action.payload;
    },
    setGuideDeliberatePractice: (state, action) => {
      state.guideDeliberatePractice = action.payload;
    },
    setGuideDeliberatePractices: (state, action) => {
      state.guideDeliberatePractices = action.payload;
    },
    setGuideInstructionalCoaching: (state, action) => {
      state.guideInstructionalCoaching = action.payload;
    },
    setGuideInstructionalCoachings: (state, action) => {
      state.guideInstructionalCoachings = action.payload;
    },
    setCoachingFeed: (state, action) => {
      state.coachingFeed = action.payload;
    },
  },
});

export const {
  setMode,
  setUser,
  setUsers,
  setViewingUser,
  setToken,
  setLogin,
  setLogout,
  setOrganisation,
  setOrganisations,
  setLocation,
  setLocations,
  setLocationsAll,
  setLocationSearchResults,
  setDivision,
  setDivisions,
  setDivisionSearchResults,
  setDivisionsForOrganisation,
  setLevel,
  setLevels,
  setLevelSearchResults,
  setLevelsForOrganisation,
  setSnackbar,
  setOrganisationSearchResults,
  setRoles,
  setPrinciple,
  setPrinciples,
  setPrincipleSearchResults,
  setPrincipleQuestion,
  setPrincipleQuestions,
  setPrincipleQuestionsSearchResults,
  setAssessment,
  setAssessmentReports,
  setAssessors,
  setAssessees,
  setAssesseeReports,
  setAssessments,
  setGuide,
  setGuides,
  setProjectOverview,
  setProjectOverviews,
  setGuideDeliberatePractice,
  setGuideDeliberatePractices,
  setGuideInstructionalCoaching,
  setGuideInstructionalCoachings,
  setCoachingFeed,
} = authSlice.actions;

export default authSlice.reducer;
